import React, { useEffect, useState } from "react";
import { Box, Button, Modal } from "@mui/material";
import SearchInput from "../components/SearchInput/SearchInput";
import ResultsList from "../components/ResultsList/ResultsList";
import { searchCpv, getCpv, sendUserSearch } from "../services/cpvService";
import LoadMore from "../components/SearchInput/LoadMore";
import _ from "lodash";
import {
  subscribeEvent,
  unsubscribeEvent,
  triggerEvent
} from '../utils/events';
import {
  getCached,
  getAllCache,
  storeCache,
  removeCache,
  clearCache,
} from "../services/cacheService";

const debouncedSave = _.debounce(
  (searchValue, selectedNode) => sendUserSearch({ searchValue, selectedNode }),
  1500
);

const Search = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchKey, setSearchKey] = useState("");
  const [selected, setSelected] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [cpvs, setCpvs] = useState([]);
  const [caches, setCaches] = useState(getAllCache() || []);
  const [tempCaches, setTempCaches] = useState(getAllCache() || []);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingResults, setLoadingResults] = useState(true);
  const debouncedSearchKey = useDebounce(searchKey, 500);

  function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

  const reloadSearch = async () => {
    try {
      setLoadingResults(true);
      const sanitizedSearchKey = debouncedSearchKey.match(/^[a-zA-Z0-9\säöüÄÖÜßẞ]+$/g);
      if (!sanitizedSearchKey) {
        setError("No CPV code found");
        setLoading(false);
      return;
     }
     const { data } = await searchCpv(debouncedSearchKey);
      setHasMore(false);
      if (!Array.isArray(data) && !data?.error) {
        setError("No CPV code found");
      } else {
        setSearchResults(data);
        setError(data?.error);

      }

      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (!debouncedSearchKey) return;
    setLoading(true);
    setCpvs([]);
    reloadSearch();
  };

  const handleGetter = async (node) => {
    setSelected(node);
     setLoadingResults(false);
    setLoading(true);
    setSearchResults([]);
    await getCpv(node.code).then((res) => {
      setLoading(false);
      setHasMore(
        res.data[0]?.total_pages && res.data[0]?.total_pages > page + 1
      );
      setCpvs(res.data);
      setError(res.data?.error);
    });
  };

  const handleSearchClick = async () => {
     setLoadingResults(false);
    setCpvs([]);
    if (debouncedSearchKey.length ==0) {
      // setSearchKey('listAll');
      setLoading(true);
      setSearchResults([]);
      setPage(0);
      await getCpv('listAll').then((res) => {
        setHasMore(
         res.data[0]?.total_pages && (res.data[0]?.total_pages) > page + 1
        );
        setError("");
        setLoading(false);
        setCpvs(res.data);
      });
    }
    else if (debouncedSearchKey.length > 0 && debouncedSearchKey.length <= 2) { return; }
    else {
      const sanitizedSearchKey = debouncedSearchKey.match(/^[a-zA-Z0-9\säöüÄÖÜßẞ]+$/g);
      if (!sanitizedSearchKey) {
        setError("No CPV code found");
        setLoading(false);
      return;
     }
      setLoading(true);
      setSearchResults([]);
      setPage(0);
      await getCpv(debouncedSearchKey).then((res) => {
        setHasMore(
          res.data[0]?.total_pages && res.data[0]?.total_pages > page + 1
        );
        setLoading(false);
        setCpvs(res.data);
        setError(res.data?.error);
      });
    };
  }

  const handleCachedItem = (item, isChecked) => {
    if (isChecked) sendUserSearch({ searchValue: searchKey, selectedNode: item.code });

    const currentItemTempCacheIndex = tempCaches.findIndex(i => i.code === item.code);

    if (!!getCached(item.code)) {
      removeCache(item.code);

      if (currentItemTempCacheIndex < 0) {
        setTempCaches(prevState => [...prevState, { ...item, state: 'tempRemoved' }]);
      }

      setCaches(getAllCache());
      return false;
    }

    const data = {
      code: item.code,
      description: item.description,
    };

    storeCache(data);
    setCaches(getAllCache());

    if (currentItemTempCacheIndex < 0) {
      setTempCaches(prevState => [...prevState, { ...data, state: 'cached' }]);
    }

    return true;
  };

   const handleLoadMore = async () => {
    setLoading(true);
    const key =  searchKey || 'listAll';
    await getCpv(key, page).then((res) => {
      if (key=="listAll") {
        setHasMore(
        res.data[0]?.total_pages && (res.data[0]?.total_pages) > page + 1
      );
      } else {
        setHasMore(
        res.data[0]?.total_pages && res.data[0]?.total_pages> page + 1
      );
      }
      const data = res.data;
      setCpvs((prev) => [...prev, ...data]);
      setLoading(false);
    });
  };

    const clearSearchResult = (tag) => {
    if (tag=="remove") {
      setSearchKey("");
      setCpvs([])
      setError("");
    } 
    setSearchResults([]);
  };

  const handleSearchInputChange = (e) => {
    const searchValue = e.target.value;
    setSearchKey(searchValue);

    if (searchValue.length > 0) {
      debouncedSave(searchValue, null);
    }
  };

  const handleRemoveCpv = (e) => {
    if (e.detail?.code) {
      removeCache(e.detail?.code)
      setCaches(getAllCache())
    }
  }

  const handleSelectionConfirm = () => {
    setTempCaches([])
    triggerEvent('userFormApp:selectedCpvCode', getAllCache())
    setOpen(false);
  };

  useEffect(() => {
    setSelected(null);
  handleSearch(debouncedSearchKey);
}, [debouncedSearchKey]);

  useEffect(() => {
    if (cpvs.length > 0) handleLoadMore();
  }, [page]);

  useEffect(() => {
    subscribeEvent('userFormApp:openModal', () => setOpen(true));
    subscribeEvent('userFormApp:removeCpv', handleRemoveCpv);

    clearCache();
    setCaches([]);

    return () => {
      unsubscribeEvent("userFormApp:openModal", () => { });
      unsubscribeEvent("userFormApp:removeCpv", () => { });
    };
  }, []);


  const handleClose = (_, reason) => {
    setOpen(false)

    // On outside click of modal
    if (reason === 'backdropClick') {
      tempCaches.forEach(item => {
        if (item.state === 'tempRemoved') {
          if (!getCached(item.code)) {
            const { state, ...actualItem } = item;
            storeCache(actualItem)
          }
        } else {
          removeCache(item.code);
        }
      });
      setCaches(getAllCache())
      setTempCaches([]);
    }
  }

  return (
  <>
    <Modal open={open} onClose={handleClose}>
      <div className="cpv-search-modal">
        <Box>
          <SearchInput
            loading={loading}
            searchKey={searchKey}
            handleSearchInputChange={handleSearchInputChange}
            searchItems={searchResults}
            handleSearchClick={handleSearchClick}
            clearSearchResult={clearSearchResult}
            errorMessage={error}
            getItem={handleGetter}
            caches={caches}
            handleCachedItem={handleCachedItem}
            data={cpvs}
            hasMore={hasMore}
          />
        </Box>

        {cpvs.length > 0 && !loadingResults && (
          <Box className="cpv-search-list-wrapper">
            {searchKey.length > 2 || error === "" ? (
              <>
                <ResultsList
                  data={cpvs}
                  handleCachedItem={handleCachedItem}
                  caches={caches}
                  searchKey={debouncedSearchKey}
                  selected={selected}
                  searchItems={searchResults}
                />
                {!loadingResults && hasMore && (
                  <LoadMore
                    hasMore={hasMore}
                    setPage={setPage}
                    loading={loading}
                  />
                )}
              </>
            ) : null}
          </Box>
        )}

        <Box className="cpv-search-btn-container">
          <Button
            variant="contained"
            onClick={handleSelectionConfirm}
            disableElevation={true}
          >
            Confirm
          </Button>
        </Box>
      </div>
    </Modal>
  </>
);
};

export default Search;
